import { useEffect, useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'

const INDENT = '  '

/**
 * Re-serializes the document with one node per line. The text is rendered as
 * text, never as markup: an attachment is a file a stranger emailed us, and an
 * XML order can carry anything at all inside a CDATA section.
 */
function formatNode(node: Node, depth: number, lines: string[]): void {
  const padding = INDENT.repeat(depth)

  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.nodeValue?.trim()
    if (text) lines.push(padding + text)
    return
  }

  if (node.nodeType === Node.CDATA_SECTION_NODE) {
    const text = node.nodeValue?.trim()
    if (text) lines.push(`${padding}<![CDATA[${text}]]>`)
    return
  }

  if (node.nodeType === Node.COMMENT_NODE) {
    lines.push(`${padding}<!--${node.nodeValue ?? ''}-->`)
    return
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return

  const element = node as Element
  const attributes = Array.from(element.attributes)
    .map((attribute) => ` ${attribute.name}="${attribute.value}"`)
    .join('')

  const children = Array.from(element.childNodes).filter(
    (child) => child.nodeType !== Node.TEXT_NODE || (child.nodeValue?.trim() ?? '') !== '',
  )

  if (children.length === 0) {
    lines.push(`${padding}<${element.nodeName}${attributes} />`)
    return
  }

  // A tag holding nothing but its own text stays on one line, which is most of
  // an order's fields and the difference between a readable file and a column.
  const onlyChild = children[0]
  if (children.length === 1 && onlyChild.nodeType === Node.TEXT_NODE) {
    lines.push(`${padding}<${element.nodeName}${attributes}>${onlyChild.nodeValue?.trim() ?? ''}</${element.nodeName}>`)
    return
  }

  lines.push(`${padding}<${element.nodeName}${attributes}>`)
  for (const child of children) formatNode(child, depth + 1, lines)
  lines.push(`${padding}</${element.nodeName}>`)
}

function formatXml(source: string): string {
  const parsed = new DOMParser().parseFromString(source, 'application/xml')
  if (parsed.getElementsByTagName('parsererror').length > 0 || !parsed.documentElement) {
    return source
  }

  const lines: string[] = []
  formatNode(parsed.documentElement, 0, lines)
  return lines.join('\n')
}

export function XmlViewer({
  blob,
  full,
  maxPreviewLines = 14,
}: {
  blob: Blob | null
  full: boolean
  maxPreviewLines?: number
}) {
  const [source, setSource] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setSource(null)
    setError(null)

    if (!blob) return

    void blob
      .text()
      .then((text) => {
        if (!cancelled) setSource(text)
      })
      .catch(() => {
        if (!cancelled) setError('Could not read this file.')
      })

    return () => {
      cancelled = true
    }
  }, [blob])

  const formatted = useMemo(() => (source === null ? null : formatXml(source)), [source])

  if (error) {
    return <p className="px-4 py-6 text-center text-xs text-gray-400">{error}</p>
  }

  if (formatted === null) {
    return (
      <div className="flex h-32 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
      </div>
    )
  }

  const lines = formatted.split('\n')
  const shown = full ? lines : lines.slice(0, maxPreviewLines)

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-auto bg-white">
        <pre className="w-max px-3 py-2 font-mono text-[11px] leading-[1.6] text-gray-700">{shown.join('\n')}</pre>
      </div>
      {!full && lines.length > maxPreviewLines && (
        <p className="shrink-0 border-t border-gray-200 bg-white px-3 py-1.5 text-[11px] text-gray-400">
          {lines.length - maxPreviewLines} more lines
        </p>
      )}
    </div>
  )
}
